/*
 * Copyright (C) 2021 Bosch.IO GmbH. All rights reserved.
 */

import React, { SetStateAction } from 'react';
import { OpenBlade, BladeOptions, DefaultBladeOptions } from './types';

export function replaceOrAddBlade(
    dispatch: React.Dispatch<SetStateAction<OpenBlade[]>>,
    blade: OpenBlade,
): void {
    dispatch(s => {
        const found = s.findIndex(b => b.name === blade.name);
        if (found !== -1) {
            return updateBladeOptions(s, s[found], blade.options);
        }
        // sets a newly opened blade to active
        return [...s, blade].map(b => ({
            ...b,
            options: { ...b.options, active: b.name === blade.name },
        }));
    });
}

export function openBlade(
    dispatch: React.Dispatch<SetStateAction<OpenBlade[]>>,
    blade: OpenBlade,
): void {
    dispatch(s => {
        return [...s, blade];
    });
}

export function updateBlade(
    dispatch: React.Dispatch<SetStateAction<OpenBlade[]>>,
    name: string,
    options: BladeOptions<unknown>,
): void {
    dispatch(s => {
        const blade = s.find(b => b.name === name);

        if (blade) {
            return updateBladeOptions(s, blade, options);
        }

        return s;
    });
}

function updateBladeOptions(
    s: OpenBlade<unknown>[],
    blade: OpenBlade<unknown>,
    options: DefaultBladeOptions,
) {
    const idx = s.indexOf(blade);
    const update = {
        ...blade,
        options: { ...blade.options, ...options },
    };
    // make sure only one blade is active at a time
    const active = update.options.active;
    const merged = [...s].map(b => {
        if (active) {
            return { ...b, options: { ...b.options, active: false } };
        }
        return b;
    });
    merged.splice(idx, 1, update);
    return merged;
}

export function closeSucceeding(
    dispatch: React.Dispatch<SetStateAction<OpenBlade[]>>,
    name: string,
): void {
    dispatch(s => {
        const idx = s.findIndex(b => name === b.name);
        if (idx >= 0) {
            const set = s.slice(0, idx);
            const b = set[set.length - 1];
            set[set.length - 1] = {
                ...b,
                options: {
                    ...b.options,
                    active: true,
                    expanded: false,
                    collapsed: false,
                },
            };

            return [...set];
        }
        return s;
    });
}

export function closeBlade(
    dispatch: React.Dispatch<SetStateAction<OpenBlade[]>>,
    blade: OpenBlade,
): void {
    dispatch(s => {
        return [...s.filter(b => b.name !== blade.name)];
    });
}

export function activateBlade(
    dispatch: React.Dispatch<SetStateAction<OpenBlade[]>>,
    name: string,
): void {
    dispatch(openBlades => [
        ...openBlades.map(b => {
            return {
                ...b,
                options: {
                    ...b.options,
                    active: b.name === name,
                },
            };
        }),
    ]);
}

export function expandBlade(
    dispatch: React.Dispatch<SetStateAction<OpenBlade[]>>,
    name: string,
): void {
    let found = false;
    dispatch(s => [
        ...s.map(b => {
            if (b.name === name) {
                found = true;
                return {
                    ...b,
                    options: {
                        ...b.options,
                        expanded: true,
                        collapsed: false,
                    },
                };
            }
            return {
                ...b,
                overrides: {
                    ...b.overrides,
                    collapsed: true,
                    expanded: false,
                    flip: !found,
                },
            };
        }),
    ]);
}

export function restoreBlade(
    dispatch: React.Dispatch<SetStateAction<OpenBlade[]>>,
    name: string,
): void {
    dispatch(s => [
        ...s.map(b => {
            if (b.name === name) {
                return {
                    ...b,
                    options: {
                        ...b.options,
                        expanded: false,
                        collapsed: false,
                    },
                    overrides: {
                        expanded: undefined,
                        collapsed: undefined,
                        flip: undefined,
                    },
                };
            }

            let flip = undefined;
            if (b.options.collapsed) {
                flip = b.overrides.flip;
            }

            return {
                ...b,
                options: {
                    ...b.options,
                    expanded: false,
                },
                overrides: {
                    ...b.overrides,
                    collapsed: undefined,
                    expanded: undefined,
                    flip,
                },
            };
        }),
    ]);
}

export function collapseBlade(
    dispatch: React.Dispatch<SetStateAction<OpenBlade[]>>,
    name: string,
): void {
    dispatch(s => {
        const result = [
            ...s.map((b, index) => {
                if (b.name === name) {
                    return {
                        ...b,
                        options: {
                            ...b.options,
                            collapsed: true,
                        },
                        overrides: {
                            ...b.overrides,
                            collapsed: undefined,
                            flip: index === 0 ? true : undefined,
                        },
                    };
                } else {
                    return b;
                }
            }),
        ];

        if (result.length > 1) {
            for (let i = 1; i < result.length; i++) {
                const isPrevFlip = result[i - 1].overrides.flip;
                if (isPrevFlip && result[i].options.collapsed) {
                    result[i].overrides.flip = true;
                }
            }
        }

        return result;
    });
}
